// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// Desktop input — the keyboard half (M3-31).
//
// The controls a **shipping client** has: two flippers, a variable-power plunger, a four-way
// nudge and a pause. Nothing else. The harness's own affordances — cycle the bat tuning, cycle
// the camera, step the render tier, say the next callout, slow motion — live in
// `src/harness/input.ts` and compose on top of this, because a player has no use for a control
// that changes the physics under the ball they are playing.
//
// Two properties are load-bearing and neither is obvious:
//
//   * **Input is sampled by the simulation, not delivered to it** (PH-024). A keydown sets a
//     flag; the fixed-step loop reads that flag on whichever millisecond it reaches next. A
//     press therefore lands on an exact simulation step regardless of what the display or the
//     event queue is doing, which is what makes a tap pass a repeatable skill instead of a
//     lottery on frame timing. Nothing here calls into the game.
//   * **The flippers need both edges and the cabinet switch needs one.** That distinction is
//     the session's to make; this only reports what is held.
//
// Both real pinball key conventions are accepted, because players have strong habits: Shift for
// the flippers as on a physical cabinet's side buttons, and arrow keys for anyone who does not
// know that.
//
// **The arrow cluster is deliberately a complete set of controls** (M3-40): left and right are
// the flippers, down is the plunger and up is a nudge up the playfield. That is every control a
// player needs, under one hand, with no setting to find and nothing announced — which is a
// better answer than a "one-handed mode", because a mode is something you have to know exists
// before it can help you. A player using two hands never notices it is there.

/** What the player is doing, sampled by the simulation. */
export interface PlayControls {
  leftFlipper: boolean
  rightFlipper: boolean
  /** Latched, consumed by the loop: fire once per press rather than once per frame. */
  launch: boolean
  /** How far the plunger was pulled back for the latched launch, 0 … 1. */
  launchPower: number
  /**
   * When the plunger started being drawn back, ms on the frame clock, or null.
   *
   * Held here rather than as a live number so the input layer keeps no timer of its own; the
   * render loop already has a clock and reads the meter off this.
   */
  chargeStartedAt: number | null
  /** Nudge impulses queued since the last read, in newton-seconds. */
  nudges: [number, number][]
  /**
   * Latched: decline the high-score table (M2-19a).
   *
   * **Latched like `launch` rather than held like a flipper**, because it is a decision and not a
   * position: the machine needs to know it was pressed once, and a held button that fired every
   * frame would skip on behalf of the next qualifying player as well.
   *
   * It has no key. The other controls here are a cabinet's — flippers, plunger, nudge — and a
   * cabinet has no skip; this one exists for the on-screen button beside the letters, where the
   * player who wants it is already looking. Adding a key would put a control on the keyboard that
   * silently ends initials entry for anybody who pressed it wondering what it did.
   */
  skipInitials: boolean
  /**
   * Latched: stop the simulation, or start it again.
   *
   * The same control the performance interruption asks for (PH-023), deliberately. A player
   * whose machine has just stopped itself needs a way to start it again, and inventing a second
   * one that only appears when something has gone wrong is a control nobody has ever pressed
   * before, at the worst possible moment to be learning it.
   */
  togglePause: boolean
  /**
   * Latched: advance exactly one simulation step, then stop again (M9-18).
   *
   * **A Builder tool, and there is no key for it.** 11 §4 lists freeze and single-step among the
   * things "available only in the Builder and never in a scored game", so nothing in this file ever
   * sets it — it is here because this is the one channel into the session, and a second channel for
   * one boolean would be a second way for the loop to be told what to do.
   */
  singleStep: boolean
  /**
   * Latched: the start button on the front lockdown bar (06 §2.4).
   *
   * **XR only, and it has no key.** The other cabinet controls here are ones a desktop can
   * plausibly stand in for; a start button is not, because on a flat screen the plunger already
   * plays all three of `pressLaunchButton`'s roles and a second control for one of them would be
   * two ways to do one thing. In a headset the button is a physical object the player reaches for,
   * so it needs somewhere to arrive.
   */
  start: boolean
  /**
   * Latched: a coin paid into the coin door (06 §2.4).
   *
   * "Decorative but functional as a gesture: pressing the coin slot adds a credit in free play,
   * with the correct mechanical clunk." The clunk is `cabinetRig`'s; this is the credit, and it
   * goes through the session rather than straight to `Game` because `credits` is inside
   * `Game.fingerprint` — a credit added outside the trace is a checkpoint no validator can
   * reproduce.
   */
  coin: boolean
}

/** One key-press of nudge, in newton-seconds against the machine frame. */
export const NUDGE_IMPULSE = 26

/** A flick of the key still puts the ball in play, it just does not put it far. */
export const PLUNGE_MIN = 0.15

/** Hold this long and the plunger is all the way back. */
export const PLUNGE_FULL_MS = 800

/**
 * One thing a player can do, named once (M3-56).
 *
 * **The names exist so that the keyboard and the guide that describes it cannot disagree.** Before
 * this there was no guide, and the reason it took until now to build one is instructive: the
 * bindings were four `e.code` comparisons scattered through a key handler, so any guide would have
 * been a *second* hand-written list of the same facts. This product already carries that scar —
 * `client/cabinet.ts` exists because a stylesheet held its own copy of the flipper geometry — and a
 * legend that drifts from the keys it describes is worse than no legend, because a player who
 * presses what it says and gets nothing concludes the machine is broken.
 */
export type ControlId =
  | 'leftFlipper'
  | 'rightFlipper'
  | 'plunger'
  | 'nudgeLeft'
  | 'nudgeRight'
  | 'nudgeUp'
  | 'nudgeDown'
  | 'pause'

/** What drives one control, and what a guide should say about it. */
export interface KeyBinding {
  readonly control: ControlId
  /**
   * Every `KeyboardEvent.code` that works. Order means nothing here — the handler asks a set.
   */
  readonly codes: readonly string[]
  /**
   * The subset a guide draws as keycaps, in the order it draws them.
   *
   * **Not every code, deliberately.** `NumpadEnter` is `Enter` with a different name and drawing
   * both would say there are two keys; `Z` and `/` are there for players who already have the
   * habit from a real cabinet and do not need to be taught it. A guide that listed all four
   * launch keys would be a guide somebody has to read rather than glance at. Held to a subset of
   * `codes` by a test, which is the whole drift guard: a key nobody can press cannot be drawn.
   */
  readonly caps: readonly string[]
  /**
   * Held rather than tapped — how long the key is down is part of what it means.
   *
   * **This is the field the whole guide exists for.** A player who taps Space gets `PLUNGE_MIN`,
   * the weakest launch in the game, on every ball for ever, and NEON NOVA's four lane awards are
   * only collectable in a narrow band near the bottom of the power range. So they are locked out
   * of a scoring feature by a fact nobody ever told them. It is the exact desktop twin of the
   * *drag*, which `firstRun.ts` calls "the one thing on this machine nobody guesses" and which
   * touch players have been taught since M3-51.
   */
  readonly held: boolean
}

/**
 * Every shipping control, in the order a guide lists them.
 *
 * `ArrowDown` is on the plunger for **one-handed play** (M3-40), and it is a binding rather than a
 * mode because a mode is a thing to find, switch on, and be told about. The arrow cluster is
 * already a complete left and right flipper pair sitting under one hand; adding the plunger below
 * them and a nudge above makes it a whole set of controls, for everybody, with nothing to
 * configure. A player using two hands never notices, and a player using one never has to ask.
 */
export const KEY_BINDINGS: readonly KeyBinding[] = [
  {
    control: 'leftFlipper',
    codes: ['ArrowLeft', 'ShiftLeft', 'KeyZ'],
    // The arrow leads, and the Shift a real cabinet teaches follows it. That order is for the
    // player who does not already know the convention, which is the player a guide is for.
    caps: ['ArrowLeft', 'ShiftLeft'],
    held: false,
  },
  {
    control: 'rightFlipper',
    codes: ['ArrowRight', 'ShiftRight', 'Slash'],
    caps: ['ArrowRight', 'ShiftRight'],
    held: false,
  },
  {
    control: 'plunger',
    codes: ['Space', 'Enter', 'NumpadEnter', 'ArrowDown'],
    caps: ['Space'],
    held: true,
  },
  // W A S D, in the order the keys sit under a hand rather than alphabetically: this cluster is
  // read as a shape, and A W S D would be four keys a player has to work out.
  { control: 'nudgeUp', codes: ['KeyW', 'ArrowUp'], caps: ['KeyW'], held: false },
  { control: 'nudgeLeft', codes: ['KeyA'], caps: ['KeyA'], held: false },
  { control: 'nudgeDown', codes: ['KeyS'], caps: ['KeyS'], held: false },
  { control: 'nudgeRight', codes: ['KeyD'], caps: ['KeyD'], held: false },
  { control: 'pause', codes: ['KeyP'], caps: ['KeyP'], held: false },
]

/** The table as a lookup, built once. */
const BY_CONTROL: ReadonlyMap<ControlId, ReadonlySet<string>> = new Map(
  KEY_BINDINGS.map((binding) => [binding.control, new Set(binding.codes)]),
)

/**
 * Does this event drive that control?
 *
 * Private, and there is deliberately no exported `codesFor` beside it: `KEY_BINDINGS` is already
 * the public answer to "which keys work", and a second accessor onto the same map would be a
 * second thing to keep agreeing with it. `check-reachable.mjs` caught the first draft of exactly
 * that — an export nothing but a test called.
 */
function drives(event: KeyboardEvent, control: ControlId): boolean {
  return BY_CONTROL.get(control)?.has(event.code) === true
}

/**
 * Plunger strength from how long the key was held.
 *
 * A real plunger is drawn back a variable distance and released, and that distance is the whole
 * skill shot — NEON NOVA's four lane awards are only collectable in a narrow band near the
 * bottom of the range. A launch control with one strength is a machine with no skill shot.
 */
export function plungerPower(heldMs: number): number {
  if (!(heldMs > 0)) return PLUNGE_MIN
  const t = heldMs >= PLUNGE_FULL_MS ? 1 : heldMs / PLUNGE_FULL_MS
  return PLUNGE_MIN + (1 - PLUNGE_MIN) * t
}

/** Everything unpressed. A fresh set of controls, and what a detach resets to. */
export function idleControls(): PlayControls {
  return {
    leftFlipper: false,
    rightFlipper: false,
    launch: false,
    launchPower: 1,
    chargeStartedAt: null,
    nudges: [],
    togglePause: false,
    singleStep: false,
    skipInitials: false,
    start: false,
    coin: false,
  }
}

/** Is this key event aimed at something the player is typing into? */
export function isTyping(target: EventTarget | null): boolean {
  const element = target as { tagName?: string; isContentEditable?: boolean } | null
  if (element === null || element === undefined) return false
  if (element.isContentEditable === true) return true
  const tag = element.tagName
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT'
}

/**
 * What a keyboard listener needs, so a test can supply it without a window.
 *
 * Typed as plain `Event` rather than `KeyboardEvent` because that is what `Window`'s
 * string-keyed overload takes, and a narrower signature here does not type-check against a real
 * window at the one call site that matters.
 */
export interface KeyboardTarget {
  addEventListener: (type: string, listener: (event: Event) => void) => void
  removeEventListener: (type: string, listener: (event: Event) => void) => void
}

export interface DesktopControlsOptions {
  /** Mutate this instead of a new object, so a harness can hold a superset of it. */
  readonly controls?: PlayControls
  /** The clock the plunger is timed against. Overridable so a test does not need one. */
  readonly now?: () => number
}

/**
 * Attach the shipping keyboard to a window, and return how to take it off again.
 *
 * **The detach is not decoration.** A React route unmounts, and a listener left on `window`
 * holds the whole controls object alive and keeps flipping a machine nobody is looking at — and
 * the next mount adds a second listener, so every key press is delivered twice. The harness
 * never needed this because the harness is one page that never unmounts; the shell is not.
 */
export function attachDesktopControls(
  target: KeyboardTarget,
  options: DesktopControlsOptions = {},
): { controls: PlayControls; detach: () => void } {
  const controls = options.controls ?? idleControls()
  const clock = options.now ?? (() => performance.now())

  const onKeyDown = (event: Event): void => {
    const e = event as KeyboardEvent
    // A settings panel has text fields in it, and Space is the plunger. Without this, typing a
    // machine name into the Builder launches the ball — and the arrow keys, which every text
    // field uses to move the caret, throw both flippers.
    if (isTyping(e.target) || e.repeat) return

    if (drives(e, 'leftFlipper')) {
      controls.leftFlipper = true
      e.preventDefault()
    }
    if (drives(e, 'rightFlipper')) {
      controls.rightFlipper = true
      e.preventDefault()
    }
    // Pull back on the way down, fire on the way up. Holding the key is drawing the plunger.
    if (drives(e, 'plunger')) {
      if (controls.chargeStartedAt === null) controls.chargeStartedAt = clock()
      e.preventDefault()
    }
    // Nudge. The impulse goes into the machine frame, never into a ball (PH-070), so a ball
    // touching nothing is genuinely unaffected however hard the key is hit.
    if (drives(e, 'nudgeLeft')) controls.nudges.push([-NUDGE_IMPULSE, 0])
    if (drives(e, 'nudgeRight')) controls.nudges.push([NUDGE_IMPULSE, 0])
    if (drives(e, 'nudgeDown')) controls.nudges.push([0, -NUDGE_IMPULSE])
    if (drives(e, 'nudgeUp')) {
      controls.nudges.push([0, NUDGE_IMPULSE])
      // `ArrowUp` is the fourth key of the one-handed cluster — up the playfield, the nudge that
      // saves a ball and the only one worth having if you are only getting one. It scrolls the
      // page and so is the one that has to be swallowed; `W` does not and is left alone.
      if (e.code === 'ArrowUp') e.preventDefault()
    }
    if (drives(e, 'pause')) controls.togglePause = true
  }

  const onKeyUp = (event: Event): void => {
    const e = event as KeyboardEvent
    // Deliberately **not** guarded by `isTyping`. A player who pressed a flipper and then
    // clicked into a field would otherwise never get the key-up, and the bat would stay held
    // for the rest of the game.
    if (drives(e, 'leftFlipper')) controls.leftFlipper = false
    if (drives(e, 'rightFlipper')) controls.rightFlipper = false
    if (drives(e, 'plunger') && controls.chargeStartedAt !== null) {
      controls.launchPower = plungerPower(clock() - controls.chargeStartedAt)
      controls.chargeStartedAt = null
      controls.launch = true
      e.preventDefault()
    }
  }

  // Losing focus mid-flip would otherwise leave a flipper held up forever.
  const onBlur = (): void => {
    controls.leftFlipper = false
    controls.rightFlipper = false
    // Let go of the plunger too, without firing it: a tab switch is not a shot.
    controls.chargeStartedAt = null
  }

  target.addEventListener('keydown', onKeyDown)
  target.addEventListener('keyup', onKeyUp)
  target.addEventListener('blur', onBlur)

  return {
    controls,
    detach: () => {
      target.removeEventListener('keydown', onKeyDown)
      target.removeEventListener('keyup', onKeyUp)
      target.removeEventListener('blur', onBlur)
      onBlur()
    },
  }
}
